/* ==========================================================================
   Product comparison store (browser only, max 4) + <compare-bar> tray.
   Cards carry <input data-compare-toggle="handle" data-title data-image>.
   ========================================================================== */
(() => {
  const { theme } = window;
  if (theme.compare) return;
  const KEY = 'xh-compare';
  const MAX = 4;

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {
      /* ignore */
    }
    theme.events.emit('compare:updated', { list });
  };

  theme.compare = {
    MAX,
    all: read,
    has: (handle) => read().some((item) => item.handle === handle),
    add(item) {
      const list = read();
      if (list.some((i) => i.handle === item.handle)) return true;
      if (list.length >= MAX) {
        theme.toast({ message: theme.strings.compareMax, type: 'error' });
        return false;
      }
      write([...list, item]);
      return true;
    },
    remove(handle) {
      write(read().filter((item) => item.handle !== handle));
    },
    clear() {
      write([]);
    },
  };

  /* Keep every checkbox in sync */
  const sync = () => {
    const handles = new Set(read().map((i) => i.handle));
    document.querySelectorAll('[data-compare-toggle]').forEach((input) => {
      input.checked = handles.has(input.dataset.compareToggle);
    });
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-compare-toggle]');
    if (!input) return;
    if (input.checked) {
      const ok = theme.compare.add({
        handle: input.dataset.compareToggle,
        title: input.dataset.title,
        image: input.dataset.image,
      });
      if (!ok) input.checked = false;
    } else {
      theme.compare.remove(input.dataset.compareToggle);
    }
  });

  theme.events.on('compare:updated', sync);
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) theme.events.emit('compare:updated', { list: read() });
  });
  // Cards added later (load more, filters, recently viewed)
  new MutationObserver(theme.utils.debounce(sync, 120)).observe(document.body, { childList: true, subtree: true });

  /* ---------- <compare-bar> ---------- */
  if (!customElements.get('compare-bar')) {
    customElements.define(
      'compare-bar',
      class CompareBar extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-compare-items]');
          this.count = this.querySelector('[data-compare-count]');
          this.go = this.querySelector('[data-compare-go]');
          this.querySelector('[data-compare-clear]')?.addEventListener('click', () => theme.compare.clear());
          this.list.addEventListener('click', (event) => {
            const remove = event.target.closest('[data-remove-compare]');
            if (remove) theme.compare.remove(remove.dataset.removeCompare);
          });
          theme.events.on('compare:updated', () => this.render());
          this.render();
        }

        render() {
          const items = read();
          const onComparePage = Boolean(document.querySelector('compare-page'));
          this.hidden = items.length === 0 || onComparePage;
          document.body.classList.toggle('has-compare-bar', !this.hidden);
          this.count.textContent = `(${items.length}/${MAX})`;
          this.go.classList.toggle('is-disabled', items.length < 2);
          this.go.setAttribute('aria-disabled', String(items.length < 2));
          this.list.replaceChildren(
            ...items.map((item) => {
              const li = document.createElement('li');
              li.className = 'compare-bar__item';
              const img = document.createElement('img');
              img.src = item.image || '';
              img.alt = item.title || '';
              img.width = 48;
              img.height = 48;
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'compare-bar__remove';
              button.dataset.removeCompare = item.handle;
              button.setAttribute('aria-label', `${theme.strings.compareRemove} ${item.title}`);
              button.innerHTML = '<svg class="icon icon--sm" aria-hidden="true" focusable="false"><use href="#icon-close"></use></svg>';
              li.append(img, button);
              return li;
            })
          );
        }
      }
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
